/**
 * Word Fluency Tap content: types, level params, local item bank, round builder.
 * Spec: items { id, label, imageUrl, categories: string[] }; contentSeed selects target + distractors.
 */

import {
  filterEligibleTargetCategories,
  isConfusingWordFluencyDistractor,
} from "./wordFluencyCategoryOverrides";

export interface WordFluencyTapItem {
  id: string;
  label: string;
  imageUrl: string;
  categories: string[];
  /**
   * Optional sentence narrated by the in-game avatar overlay (Phase 1
   * voice-over feature). Falls back to the i18n key
   * `games.contentBank.assets.voiceDescriptions.{itemId}` and ultimately
   * to the localized item label when undefined or empty.
   */
  voiceDescription?: string | null;
}

/** Backend can return items or tiles + targetCategory */
export interface WordFluencyTapContentResponse {
  items?: WordFluencyTapItem[];
  tiles?: WordFluencyTapItem[];
  targetCategory?: string;
  targetCategoryLabel?: string;
}

export interface LevelParams {
  tileCount: number;
  timeSeconds: number;
  showLabels: boolean;
  /** simple | subcategory | twoStep */
  categoryComplexity: "simple" | "subcategory" | "twoStep";
}

export interface RoundContent {
  tiles: WordFluencyTapItem[];
  targetCategory: string;
  /** Translation key for category display, e.g. games.wordFluencyTap.categories.animals */
  targetCategoryLabelKey: string;
}

/** Seeded RNG in [0, 1) for deterministic rounds */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

export function getLevelParams(level: number): LevelParams {
  const clamped = Math.max(1, Math.min(10, level));
  if (clamped <= 2) {
    return { tileCount: 12, timeSeconds: 100, showLabels: true, categoryComplexity: "simple" };
  }
  if (clamped <= 4) {
    return { tileCount: 15, timeSeconds: 85, showLabels: true, categoryComplexity: "simple" };
  }
  if (clamped <= 6) {
    return { tileCount: 16, timeSeconds: 85, showLabels: true, categoryComplexity: "subcategory" };
  }
  if (clamped <= 8) {
    return { tileCount: 18, timeSeconds: 68, showLabels: false, categoryComplexity: "simple" };
  }
  return { tileCount: 20, timeSeconds: 52, showLabels: false, categoryComplexity: "twoStep" };
}

/** Local content bank: items with categories[]; some items in multiple categories for overlap */
const LOCAL_ITEMS: WordFluencyTapItem[] = [
  // Animals
  { id: "cat", label: "Cat", imageUrl: "", categories: ["animals"] },
  { id: "dog", label: "Dog", imageUrl: "", categories: ["animals"] },
  { id: "bird", label: "Bird", imageUrl: "", categories: ["animals"] },
  { id: "fish", label: "Fish", imageUrl: "", categories: ["animals"] },
  { id: "rabbit", label: "Rabbit", imageUrl: "", categories: ["animals"] },
  { id: "horse", label: "Horse", imageUrl: "", categories: ["animals"] },
  { id: "mouse", label: "Mouse", imageUrl: "", categories: ["animals"] },
  { id: "lion", label: "Lion", imageUrl: "", categories: ["animals"] },
  { id: "bear", label: "Bear", imageUrl: "", categories: ["animals"] },
  { id: "owl", label: "Owl", imageUrl: "", categories: ["animals"] },
  { id: "chicken", label: "Chicken", imageUrl: "", categories: ["animals", "food"] },
  { id: "pig", label: "Pig", imageUrl: "", categories: ["animals"] },
  { id: "cow", label: "Cow", imageUrl: "", categories: ["animals"] },
  { id: "duck", label: "Duck", imageUrl: "", categories: ["animals"] },
  { id: "frog", label: "Frog", imageUrl: "", categories: ["animals"] },
  { id: "bee", label: "Bee", imageUrl: "", categories: ["animals"] },
  { id: "elephant", label: "Elephant", imageUrl: "", categories: ["animals"] },
  { id: "monkey", label: "Monkey", imageUrl: "", categories: ["animals"] },
  { id: "tiger", label: "Tiger", imageUrl: "", categories: ["animals"] },
  { id: "sheep", label: "Sheep", imageUrl: "", categories: ["animals"] },
  // Furniture / household
  { id: "chair", label: "Chair", imageUrl: "", categories: ["furniture"] },
  { id: "table", label: "Table", imageUrl: "", categories: ["furniture"] },
  { id: "lamp", label: "Lamp", imageUrl: "", categories: ["furniture"] },
  { id: "bed", label: "Bed", imageUrl: "", categories: ["furniture"] },
  { id: "sofa", label: "Sofa", imageUrl: "", categories: ["furniture"] },
  { id: "clock", label: "Clock", imageUrl: "", categories: ["furniture"] },
  { id: "book", label: "Book", imageUrl: "", categories: ["furniture"] },
  { id: "cup", label: "Cup", imageUrl: "", categories: ["furniture"] },
  { id: "key", label: "Key", imageUrl: "", categories: ["furniture"] },
  { id: "phone", label: "Phone", imageUrl: "", categories: ["furniture"] },
  { id: "mirror", label: "Mirror", imageUrl: "", categories: ["furniture"] },
  { id: "vase", label: "Vase", imageUrl: "", categories: ["furniture"] },
  { id: "rug", label: "Rug", imageUrl: "", categories: ["furniture"] },
  { id: "shelf", label: "Shelf", imageUrl: "", categories: ["furniture"] },
  { id: "drawer", label: "Drawer", imageUrl: "", categories: ["furniture"] },
  // Food
  { id: "apple", label: "Apple", imageUrl: "", categories: ["food"] },
  { id: "bread", label: "Bread", imageUrl: "", categories: ["food"] },
  { id: "cheese", label: "Cheese", imageUrl: "", categories: ["food"] },
  { id: "egg", label: "Egg", imageUrl: "", categories: ["food"] },
  { id: "milk", label: "Milk", imageUrl: "", categories: ["food"] },
  { id: "orange", label: "Orange", imageUrl: "", categories: ["food"] },
  { id: "tomato", label: "Tomato", imageUrl: "", categories: ["food"] },
  { id: "cake", label: "Cake", imageUrl: "", categories: ["food"] },
  { id: "carrot", label: "Carrot", imageUrl: "", categories: ["food"] },
  { id: "banana", label: "Banana", imageUrl: "", categories: ["food"] },
  { id: "potato", label: "Potato", imageUrl: "", categories: ["food"] },
  { id: "rice", label: "Rice", imageUrl: "", categories: ["food"] },
  { id: "soup", label: "Soup", imageUrl: "", categories: ["food"] },
  { id: "fish_food", label: "Fish", imageUrl: "", categories: ["food"] },
  { id: "meat", label: "Meat", imageUrl: "", categories: ["food"] },
  // Vehicles
  { id: "car", label: "Car", imageUrl: "", categories: ["vehicles"] },
  { id: "bus", label: "Bus", imageUrl: "", categories: ["vehicles"] },
  { id: "bike", label: "Bike", imageUrl: "", categories: ["vehicles"] },
  { id: "boat", label: "Boat", imageUrl: "", categories: ["vehicles"] },
  { id: "plane", label: "Plane", imageUrl: "", categories: ["vehicles"] },
  { id: "train", label: "Train", imageUrl: "", categories: ["vehicles"] },
  { id: "truck", label: "Truck", imageUrl: "", categories: ["vehicles"] },
  { id: "ship", label: "Ship", imageUrl: "", categories: ["vehicles"] },
  { id: "helicopter", label: "Helicopter", imageUrl: "", categories: ["vehicles"] },
  { id: "motorcycle", label: "Motorcycle", imageUrl: "", categories: ["vehicles"] },
  { id: "scooter", label: "Scooter", imageUrl: "", categories: ["vehicles"] },
  { id: "submarine", label: "Submarine", imageUrl: "", categories: ["vehicles"] },
  // Clothing
  { id: "shirt", label: "Shirt", imageUrl: "", categories: ["clothing"] },
  { id: "pants", label: "Pants", imageUrl: "", categories: ["clothing"] },
  { id: "hat", label: "Hat", imageUrl: "", categories: ["clothing"] },
  { id: "shoes", label: "Shoes", imageUrl: "", categories: ["clothing"] },
  { id: "coat", label: "Coat", imageUrl: "", categories: ["clothing"] },
  { id: "socks", label: "Socks", imageUrl: "", categories: ["clothing"] },
  { id: "dress", label: "Dress", imageUrl: "", categories: ["clothing"] },
  { id: "scarf", label: "Scarf", imageUrl: "", categories: ["clothing"] },
  { id: "gloves", label: "Gloves", imageUrl: "", categories: ["clothing"] },
  { id: "jacket", label: "Jacket", imageUrl: "", categories: ["clothing"] },
  // Nature
  { id: "tree", label: "Tree", imageUrl: "", categories: ["nature"] },
  { id: "flower", label: "Flower", imageUrl: "", categories: ["nature"] },
  { id: "sun", label: "Sun", imageUrl: "", categories: ["nature"] },
  { id: "moon", label: "Moon", imageUrl: "", categories: ["nature"] },
  { id: "star", label: "Star", imageUrl: "", categories: ["nature"] },
  { id: "cloud", label: "Cloud", imageUrl: "", categories: ["nature"] },
  { id: "leaf", label: "Leaf", imageUrl: "", categories: ["nature"] },
  { id: "grass", label: "Grass", imageUrl: "", categories: ["nature"] },
  { id: "mountain", label: "Mountain", imageUrl: "", categories: ["nature"] },
  { id: "river", label: "River", imageUrl: "", categories: ["nature"] },
];

const CATEGORY_KEYS = ["animals", "furniture", "food", "vehicles", "clothing", "nature"] as const;

function shuffleWithSeed<T>(arr: T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function getCategoriesInPool(items: WordFluencyTapItem[]): string[] {
  const set = new Set<string>();
  for (const item of items) {
    for (const c of item.categories) {
      set.add(c);
    }
  }
  return Array.from(set);
}

export interface BuildRoundOptions {
  /** When set, use content-bank category labels (backend content). Otherwise use wordFluencyTap categories (local pool). */
  categoryLabelSource?: "contentBank" | "wordFluencyTap";
  /** Optional: force target category if present in pool (e.g. from backend). */
  targetCategory?: string;
  /** Patient difficulty level (1–10); constrains which categories can be round targets. */
  level?: number;
}

function normalizePoolItems(items: WordFluencyTapItem[]): WordFluencyTapItem[] {
  return items.filter((item) => item.categories.length > 0);
}

function pickTargetCategory(
  categoriesInPool: string[],
  rng: () => number,
  level: number,
  forced?: string,
): string {
  const eligible = filterEligibleTargetCategories(categoriesInPool, level);
  if (
    forced &&
    (eligible.includes(forced) || categoriesInPool.includes(forced))
  ) {
    return forced;
  }
  const pool = eligible.length > 0 ? eligible : categoriesInPool;
  return pool.length > 0
    ? pool[Math.floor(rng() * pool.length)]
    : "animals";
}

function filterDistractors(
  items: WordFluencyTapItem[],
  targetCategory: string,
): WordFluencyTapItem[] {
  const clear = items.filter(
    (item) => !isConfusingWordFluencyDistractor(item.categories, targetCategory),
  );
  return clear.length > 0 ? clear : items;
}

/** Deduplicate by id so each item appears at most once in a round */
function dedupePoolById(items: WordFluencyTapItem[]): WordFluencyTapItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

/**
 * Build round content from any item pool: pick (or use) target category, then fill
 * tileCount tiles with a mix of target items and distractors (from other categories).
 * Same logic for local and backend content. Pool is deduped by id so all tiles are unique.
 */
export function buildRoundFromPool(
  items: WordFluencyTapItem[],
  seed: string,
  roundIndex: number,
  levelParams: LevelParams,
  options?: BuildRoundOptions
): RoundContent {
  const pool = dedupePoolById(normalizePoolItems(items));
  const rng = seededRandom(`${seed}-${roundIndex}`);
  const categoriesInPool = getCategoriesInPool(pool);
  const level = options?.level ?? 10;

  const targetCategory = pickTargetCategory(
    categoriesInPool,
    rng,
    level,
    options?.targetCategory,
  );
  const useContentBankLabels = options?.categoryLabelSource === "contentBank";
  const targetCategoryLabelKey = useContentBankLabels
    ? `games.contentBank.assets.categories.${targetCategory}`
    : `games.wordFluencyTap.categories.${targetCategory}`;

  const inCategory = pool.filter((item) =>
    item.categories.includes(targetCategory)
  );
  const notInCategory = filterDistractors(
    pool.filter((item) => !item.categories.includes(targetCategory)),
    targetCategory,
  );

  let numTarget: number;
  let numDistractor: number;
  if (notInCategory.length === 0) {
    numTarget = Math.min(levelParams.tileCount, inCategory.length);
    numDistractor = 0;
  } else {
    numTarget = Math.max(
      3,
      Math.min(
        inCategory.length,
        Math.floor(levelParams.tileCount * (0.35 + rng() * 0.25))
      )
    );
    numDistractor = levelParams.tileCount - numTarget;
  }

  const shuffledIn = shuffleWithSeed(inCategory, rng);
  const shuffledOut = shuffleWithSeed(notInCategory, rng);
  const chosenIn = shuffledIn.slice(0, numTarget);
  const chosenOut = shuffledOut.slice(0, numDistractor);
  const combined = shuffleWithSeed([...chosenIn, ...chosenOut], rng);

  return {
    tiles: combined,
    targetCategory,
    targetCategoryLabelKey,
  };
}

/**
 * Build round content from local bank (uses buildRoundFromPool with LOCAL_ITEMS).
 */
export function getRoundContent(
  seed: string,
  roundIndex: number,
  levelParams: LevelParams
): RoundContent {
  return buildRoundFromPool(
    LOCAL_ITEMS,
    seed,
    roundIndex,
    levelParams
  );
}

/** Backend tile may use itemId instead of id */
function hasValidTileId(tile: Record<string, unknown>): boolean {
  const id = tile?.id ?? tile?.itemId;
  return typeof id === "string";
}

function isWordFluencyTapContentResponse(
  data: unknown
): data is WordFluencyTapContentResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  const list = Array.isArray(d.items) ? d.items : Array.isArray(d.tiles) ? d.tiles : null;
  if (!list || list.length < 12) return false;
  const first = list[0] as Record<string, unknown>;
  return (
    hasValidTileId(first) &&
    typeof first?.label === "string" &&
    Array.isArray(first?.categories)
  );
}

/** Map backend tile (itemId) to WordFluencyTapItem (id) */
function mapBackendTileToItem(x: Record<string, unknown>): WordFluencyTapItem | null {
  const id = (x.id ?? x.itemId) as string;
  const voice = x.voiceDescription;
  const categories = Array.isArray(x.categories) ? (x.categories as string[]) : [];
  if (categories.length === 0) {
    return null;
  }
  return {
    id,
    label: (x.label as string) ?? "",
    imageUrl: (x.imageUrl as string) ?? "",
    categories,
    voiceDescription: typeof voice === "string" ? voice : null,
  };
}

/**
 * Resolve round content: backend response if valid (use same mix of targets + distractors
 * via buildRoundFromPool), else local getRoundContent.
 * Pass sessionSeed so each session gets different tiles (stable within session, varies across sessions).
 */
export function resolveRoundContent(
  contentSeed: string,
  roundIndex: number,
  levelParams: LevelParams,
  backendData: unknown,
  backendError: unknown,
  sessionSeed?: string,
  level?: number
): RoundContent {
  const effectiveSeed = sessionSeed
    ? (contentSeed ? `${contentSeed}-${sessionSeed}` : sessionSeed)
    : contentSeed || `local-${roundIndex}-${Date.now()}`;
  const roundOptions = { level };
  if (
    !backendError &&
    isWordFluencyTapContentResponse(backendData)
  ) {
    const backend = backendData as Record<string, unknown>;
    const rawList = (backend.items ?? backend.tiles) as Record<string, unknown>[] | undefined;
    if (rawList && rawList.length >= levelParams.tileCount) {
      const pool = rawList
        .map((t) => mapBackendTileToItem(t))
        .filter((item): item is WordFluencyTapItem => item !== null);
      if (pool.length >= levelParams.tileCount) {
        return buildRoundFromPool(
          pool,
          effectiveSeed,
          roundIndex,
          levelParams,
          {
            categoryLabelSource: "contentBank",
            targetCategory: backend.targetCategory as string | undefined,
            level,
          }
        );
      }
    }
  }
  return buildRoundFromPool(
    LOCAL_ITEMS,
    effectiveSeed,
    roundIndex,
    levelParams,
    roundOptions,
  );
}

/**
 * Per-round scoring for Word Fluency Tap.
 *
 * Compares which tiles the user selected against the set of target-category
 * tiles. Hits, false taps, and misses all count toward the denominator, so
 * selecting every tile drops accuracy because false taps dilute it.
 */
export interface WordFluencyRoundMetrics {
  accuracy: number;
  correctTaps: number;
  incorrectTaps: number;
  misses: number;
  errorCount: number;
}

export function computeWordFluencyRoundMetrics(
  tiles: WordFluencyTapItem[],
  targetCategory: string,
  selectedTileIds: ReadonlySet<string>,
): WordFluencyRoundMetrics {
  let correctTaps = 0;
  let incorrectTaps = 0;
  let misses = 0;
  for (const tile of tiles) {
    const isTarget = tile.categories.includes(targetCategory);
    const tapped = selectedTileIds.has(tile.id);
    if (isTarget && tapped) correctTaps++;
    else if (!isTarget && tapped) incorrectTaps++;
    else if (isTarget && !tapped) misses++;
  }
  const total = correctTaps + incorrectTaps + misses;
  const accuracy = total > 0 ? correctTaps / total : 0;
  return {
    accuracy,
    correctTaps,
    incorrectTaps,
    misses,
    errorCount: incorrectTaps + misses,
  };
}
